import { useEffect, useState, useMemo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

import { SpacesAsGrid } from '../../../modules/vendor/my-spaces/components/spaces-as-grid.component';
import { toggleConfirmModal } from '../../../redux/vendor/ui/uiActions';
import ConfirmDialog from '../../../common/components/ConfirmModal';
import { PageContainer } from '../../../common/components/PageContainer';
import { MainHeight, TopNavBarHeight } from '../../../assets/constants.css';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { setMaxSpaces, setSpaces } from '../../../redux/vendor/spaces/spacesActions';
import { useSpacesApi } from '../../../common/hooks/use-spaces-api.hook';
import { config as globalConfig } from '../../../common/hooks/use-settings-api.hook';
import logger from '../../../utils/logger';
import { AuthContext } from '../../../providers/AuthProvider';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { LimitBadge } from '../../../components/LimitBadge';

/**
 * One row of the category rail.
 *
 * The rail is a list of space types plus a "my spaces only" switch, all of
 * which are the same control with a different label, so they share one
 * definition rather than three copies of the same selected/hover rules.
 */
const FilterRow = ({
    label,
    count,
    selected,
    onClick,
}: {
    label: string;
    count?: number;
    selected: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={cn(
            'flex w-full items-center justify-between border px-3 py-2 text-start text-sm transition-colors',
            selected
                ? 'border-foreground bg-muted text-foreground'
                : 'border-transparent text-foreground hover:bg-accent',
        )}
    >
        <span className={cn(selected && 'font-medium')}>{label}</span>
        {count !== undefined && (
            <span className="ms-2 inline-flex h-5 min-w-6 items-center justify-center bg-muted px-1 text-xs text-muted-foreground">
                {count}
            </span>
        )}
    </button>
);

export const MySpaces = ({ inTab = false }: { inTab?: boolean } = {}) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const { spaces, maxSpaces } = useSelector((state: VendorState) => state.spaces);
    const { getLimit } = useVendorPlan();
    const [filteredSpaces, setFilteredSpaces] = useState(spaces);
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [showOnlyVendorSpaces, setShowOnlyVendorSpaces] = useState(false);
    const [modalProps, setModalProps] = useState({});
    const spacesAPI = useSpacesApi();

    useEffect(() => {
        fetchData()
    }, []);

    const fetchData = async () => {
        const allSpaces = await spacesAPI.findAllSpaces();
        // Backend (backoffice strategy) now scopes to public + associated
        // and strips visitor uploads, so we trust the response as-is.
        const list = allSpaces?.data ?? [];

        dispatch(setSpaces(list));
        // Resolution priority for maxSpaces:
        //   1. Plan-driven limit via useVendorPlan (already respects vendor.maxSpaces override).
        //      -1 means "unlimited" — surface as a very large number so the existing
        //      percentage-based UI degrades cleanly.
        //   2. Legacy: spaces API response (vendor's quota field if exposed).
        //   3. Legacy: cached global config.
        //   4. Hard fallback: 3.
        const planLimit = getLimit('maxSpaces');
        const planMax = planLimit === -1 ? Number.MAX_SAFE_INTEGER : (planLimit > 0 ? planLimit : null);
        const responseMax = (allSpaces?.maxSpaces && allSpaces.maxSpaces > 0) ? allSpaces.maxSpaces : null;
        const configMax = (globalConfig?.maxSpaces && globalConfig.maxSpaces > 0) ? globalConfig.maxSpaces : null;
        dispatch(setMaxSpaces(planMax ?? responseMax ?? configMax ?? 3));

        // Initialize selected spaces with already assigned spaces
        const initialSelectedSpaces = list.filter(space => space.isAssigned);
        setSelectedSpaces(initialSelectedSpaces);
    };

    useEffect(() => {
        setFilteredSpaces(spaces);
    }, [spaces]);

    // Extract unique space types
    const spaceTypes = useMemo(() => {
        const types = spaces.map(space => space.spaceType);
        return ['all', ...Array.from(new Set(types))];
    }, [spaces]);

    const handleValidateSelection = () => {
        dispatch(toggleConfirmModal())
        setModalProps({
            title: t("PAGES.MY_SPACES.CONFIRM.VALIDATE_TITLE"),
            description: t("PAGES.MY_SPACES.CONFIRM.VALIDATE_DESCRIPTION"),
            handleAccepted: async () => {
                try {
                    // Assign spaces and wait for completion
                    await spacesAPI.assignSpaces(selectedSpaces);

                    // After successful assignment, navigate to dashboard
                    navigate('/home/dashboard');
                } catch (error) {
                    logger.error("Error assigning spaces:", error);
                }
            }
        })
    }

    const handleFilterChange = (event) => {
        const value = event.target.value;
        setSearchFilter(value);
        filterSpaces(value, selectedType, showOnlyVendorSpaces);
    };

    const handleTypeSelect = (type) => {
        setSelectedType(type);
        filterSpaces(searchFilter, type, showOnlyVendorSpaces);
    };

    const toggleVendorSpacesFilter = () => {
        const newValue = !showOnlyVendorSpaces;
        setShowOnlyVendorSpaces(newValue);
        filterSpaces(searchFilter, selectedType, newValue);
    };

    const filterSpaces = (search, type, onlyVendorSpaces = false) => {
        let filtered = spaces;

        // Apply vendor filter if selected
        if (onlyVendorSpaces) {
            const vendorId = user?.vendor?._id;
            filtered = filtered.filter(space =>
                space.associatedVendor && space.associatedVendor === vendorId
            );
        }

        // Apply search filter
        if (search) {
            filtered = filtered.filter(space =>
                space.spaceType.toLowerCase().includes(search.toLowerCase()) ||
                (space.name && space.name.toLowerCase().includes(search.toLowerCase())) ||
                space.surfaces.some(surfaceType => surfaceType.toLowerCase().includes(search.toLowerCase()))
            );
        }

        // Apply type filter
        if (type && type !== 'all') {
            filtered = filtered.filter(space => space.spaceType === type);
        }

        setFilteredSpaces(filtered);
    };

    const clearFilters = () => {
        setSearchFilter('');
        setSelectedType('all');
        setShowOnlyVendorSpaces(false);
        setFilteredSpaces(spaces);
    };

    // Visitor uploads are free; only the vendor's own spaces count against the plan.
    const nonVisitorSpacesCount = selectedSpaces.filter(s => !s.uploadedByVisitor).length;
    const planMaxSpaces = getLimit('maxSpaces');
    const spaceLimitPercentage = maxSpaces > 0 ? (nonVisitorSpacesCount / maxSpaces) * 100 : 0;
    const atLimit = spaceLimitPercentage >= 100;
    const nearLimit = spaceLimitPercentage >= 80;

    return (
        <PageContainer hideTopNav={inTab} breadcrumbs={[
            { label: t('SIDEBAR.VISUALIZER.TITLE') },
            { label: t('SIDEBAR.VISUALIZER.MY_SELECTION') }
        ]}>
            {/* Header with Search and Validate Button */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 flex-wrap items-center gap-2">
                    <div className="relative w-full sm:w-72">
                        <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder={t('PAGES.MY_SPACES.SEARCH') || 'Search spaces...'}
                            value={searchFilter}
                            onChange={handleFilterChange}
                            className="h-9 px-8"
                        />
                        {searchFilter && (
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                aria-label={t('COMMON.CLEAR_SEARCH', 'Clear search') as string}
                                onClick={() => {
                                    setSearchFilter('');
                                    filterSpaces('', selectedType, showOnlyVendorSpaces);
                                }}
                                className="absolute end-1.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                            >
                                <X />
                            </Button>
                        )}
                    </div>

                    {/* Plan-limit usage chip — matches Content Studio's credits badge */}
                    <LimitBadge
                        current={nonVisitorSpacesCount}
                        limit={planMaxSpaces}
                        label={t('PLANS.LIMIT_BADGE.SPACES', 'spaces')}
                        compact
                    />

                    {/* Space Limit Warning - Compact */}
                    {nearLimit && (
                        <Alert
                            variant={atLimit ? 'destructive' : 'default'}
                            className={cn('hidden w-auto items-center md:grid', !atLimit && 'text-warning')}
                        >
                            <AlertTriangle />
                            <AlertDescription className={cn('whitespace-nowrap', !atLimit && 'text-warning')}>
                                {atLimit
                                    ? t('PAGES.MY_SPACES.LIMIT_REACHED_MESSAGE', { current: nonVisitorSpacesCount, max: maxSpaces })
                                    : t('PAGES.MY_SPACES.APPROACHING_LIMIT_MESSAGE', { current: nonVisitorSpacesCount, max: maxSpaces })}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                {/* Validate Button */}
                <Button
                    variant="outline"
                    className="h-9 min-w-40"
                    onClick={handleValidateSelection}
                    disabled={selectedSpaces.length === 0}
                >
                    {selectedSpaces.length > 0
                        ? `${t('PAGES.MY_SPACES.VALIDATE_SELECTION') || 'Validate Selection'} (${selectedSpaces.length})`
                        : t('PAGES.MY_SPACES.VALIDATE_SELECTION') || 'Validate Selection'
                    }
                    <ArrowRight className="rtl:rotate-180" />
                </Button>
            </div>

            {/*
             * The rail and the grid scroll independently on a wide screen, so the
             * pair is pinned to the viewport height the old layout computed. On
             * mobile they stack and the page scrolls as one.
             */}
            <div
                style={{ ['--spaces-pane-height' as any]: `calc(${MainHeight} - ${TopNavBarHeight} - 130px)` }}
                className="flex h-auto flex-col gap-6 md:h-(--spaces-pane-height) md:flex-row"
            >
                {/* Left Sidebar - Categories */}
                <div className="max-h-[400px] w-full shrink-0 overflow-y-auto rounded-xl border bg-card md:max-h-none md:w-[280px]">
                    {/* Category Header */}
                    <div className="sticky top-0 z-10 border-b bg-card px-4 py-3">
                        <p className="font-heading text-sm font-medium tracking-tight text-foreground">
                            {t('PAGES.MY_SPACES.CATEGORIES') || 'Categories'}
                        </p>
                    </div>

                    {/* Category List */}
                    <div className="flex flex-col gap-0.5 p-2">
                        {/* All Types */}
                        {spaceTypes.map((type, index) => (
                            <FilterRow
                                key={index}
                                label={type === 'all' ? (t('PAGES.MY_SPACES.ALL_TYPES') || 'All Types') : String(type)}
                                count={type === 'all' ? spaces.length : spaces.filter(s => s.spaceType === type).length}
                                selected={selectedType === type}
                                onClick={() => handleTypeSelect(type)}
                            />
                        ))}

                        <Separator className="my-2" />

                        {/* Vendor Spaces Filter */}
                        <FilterRow
                            label={t('PAGES.MY_SPACES.VENDOR_SPACES_ONLY') || 'My Spaces Only'}
                            selected={showOnlyVendorSpaces}
                            onClick={toggleVendorSpacesFilter}
                        />

                        {/* Clear Filters */}
                        {(searchFilter || selectedType !== 'all' || showOnlyVendorSpaces) && (
                            <>
                                <Separator className="my-2" />
                                <Button variant="outline" className="h-9 w-full" onClick={clearFilters}>
                                    {t('PAGES.MY_SPACES.CLEAR_FILTERS') || 'Clear Filters'}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Right Content - Spaces Grid */}
                <div className="flex-1 overflow-y-auto pe-1">
                    <SpacesAsGrid
                        spaces={spaces}
                        maxSpaces={maxSpaces}
                        selectedSpaces={selectedSpaces}
                        setSelectedSpaces={setSelectedSpaces}
                        filteredSpaces={filteredSpaces}
                    />
                </div>
            </div>

            <ConfirmDialog {...modalProps} />
        </PageContainer>
    );
};

export default MySpaces;
